import { IPersonaRuleNode, PersonaRuleOperator, isPersonaRuleGroup } from "@/api_framework/api_modals/HomeTileConfig";
import { DEFAULT_BASIC_PERSONA_LOGIC, IBasicPersonaLogic } from "../interface";
import { ALL_INCLUSIVE_PERSONA, fromPersonaRule } from "../personaExpression";

type PersonaPart = { isIncluded: boolean; operator: PersonaRuleOperator; personas: string[] };

const invertOperator = (operator: PersonaRuleOperator): PersonaRuleOperator => (operator === "AND" ? "OR" : "AND");

const toGroup = (personas: string[], value: boolean, operator: PersonaRuleOperator): IPersonaRuleNode =>
  personas.length === 1
    ? { persona: personas[0], value }
    : { operator, conditions: personas.map((persona) => ({ persona, value })) };

export const getExcludedPersonas = (logic: IBasicPersonaLogic): string[] =>
  logic.is_exclude_enabled ? logic.exclude : [];

export const toBasicPersonaRule = (logic: IBasicPersonaLogic): IPersonaRuleNode | null => {
  const included = logic.include;
  const excluded = getExcludedPersonas(logic);
  const conditions = [
    ...(included.length ? [toGroup(included, true, logic.include_operator)] : []),
    ...(excluded.length ? [toGroup(excluded, false, invertOperator(logic.exclude_operator))] : []),
  ];
  if (!conditions.length) return null;
  if (conditions.length === 1) return conditions[0];
  return {
    operator: "AND",
    conditions: conditions.flatMap((condition) =>
      isPersonaRuleGroup(condition) && condition.operator === "AND" ? condition.conditions : [condition],
    ),
  };
};

export const toBasicExpression = (logic: IBasicPersonaLogic): string => fromPersonaRule(toBasicPersonaRule(logic));

const toPart = (node: IPersonaRuleNode): PersonaPart | null => {
  if (!isPersonaRuleGroup(node)) {
    return {
      isIncluded: node.value !== false,
      operator: node.value === false ? "OR" : "AND",
      personas: [node.persona],
    };
  }
  if (!node.conditions.every((condition) => !isPersonaRuleGroup(condition))) return null;
  const leaves = node.conditions as { persona: string; value: boolean }[];
  const isIncluded = leaves[0]?.value !== false;
  if (!leaves.length || leaves.some((leaf) => (leaf.value !== false) !== isIncluded)) return null;
  return {
    isIncluded,
    operator: isIncluded ? node.operator : invertOperator(node.operator),
    personas: leaves.map((leaf) => leaf.persona),
  };
};

const mergeParts = (parts: PersonaPart[], joinOperator: PersonaRuleOperator): PersonaPart | null | undefined => {
  if (!parts.length) return undefined;
  if (parts.length === 1) return parts[0];
  const canJoin = parts.every((part) => part.personas.length === 1 || part.operator === joinOperator);
  if (!canJoin) return null;
  return { isIncluded: parts[0].isIncluded, operator: joinOperator, personas: parts.flatMap((part) => part.personas) };
};

const isAllInclusive = (part?: PersonaPart): boolean =>
  !part || (part.personas.length === 1 && part.personas[0] === ALL_INCLUSIVE_PERSONA);

const fromParts = (include?: PersonaPart, exclude?: PersonaPart): IBasicPersonaLogic => ({
  include: isAllInclusive(include) ? [] : (include?.personas ?? []),
  include_operator:
    !isAllInclusive(include) && include && include.personas.length > 1
      ? include.operator
      : DEFAULT_BASIC_PERSONA_LOGIC.include_operator,
  is_exclude_enabled: Boolean(exclude),
  exclude: exclude?.personas ?? [],
  exclude_operator:
    exclude && exclude.personas.length > 1 ? exclude.operator : DEFAULT_BASIC_PERSONA_LOGIC.exclude_operator,
});

export const toBasicPersonaLogic = (rule: IPersonaRuleNode | null | undefined): IBasicPersonaLogic | null => {
  if (!rule) return { ...DEFAULT_BASIC_PERSONA_LOGIC };

  const single = toPart(rule);
  if (single) return single.isIncluded ? fromParts(single) : fromParts(undefined, single);

  if (!isPersonaRuleGroup(rule) || rule.operator !== "AND") return null;

  const parts = rule.conditions.map(toPart);
  if (parts.some((part) => !part)) return null;

  const include = mergeParts(
    (parts as PersonaPart[]).filter((part) => part.isIncluded),
    "AND",
  );
  const exclude = mergeParts(
    (parts as PersonaPart[]).filter((part) => !part.isIncluded),
    "OR",
  );
  if (include === null || exclude === null) return null;
  return fromParts(include, exclude);
};
